'use client';
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { supabase } from '@/lib/supabase';
import CustomerDashboardLayout from '../components/CustomerDashboardLayout';

interface Order {
  id: string;
  order_number: string;
  status: string;
  total_amount: number;
  created_at: string;
  items_summary: string;
  items_count: number;
  delivery_address: string;
  payment_method: string;
  payment_status: string;
  tracking_number: string | null;
  estimated_delivery_time: string | null;
}

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-amber-100 text-amber-700',
  confirmed: 'bg-blue-100 text-blue-700',
  processing: 'bg-purple-100 text-purple-700',
  shipped: 'bg-indigo-100 text-indigo-700',
  delivered: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700',
  refunded: 'bg-gray-100 text-gray-600',
};

const STATUS_TABS = ['all', 'pending', 'confirmed', 'processing', 'shipped', 'delivered', 'cancelled'];

export default function CustomerOrdersPage() {
  const router = useRouter();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [updatedFlash, setUpdatedFlash] = useState<string | null>(null);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  const fetchOrders = useCallback(async (userId: string) => {
    const { data } = await supabase
      .from('platform_orders')
      .select('id, order_number, status, total_amount, created_at, items_summary, items_count, delivery_address, payment_method, payment_status, tracking_number, estimated_delivery_time')
      .eq('customer_id', userId)
      .order('created_at', { ascending: false });
    setOrders(data || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    let mounted = true;

    supabase.auth.getUser().then(({ data: { user } }) => {
      if (!user) { router.push('/customer-login'); return; }
      fetchOrders(user.id);

      if (!mounted) return;

      // Real-time: listen for order status updates from seller
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }

      channelRef.current = supabase
        .channel(`customer-orders-rt-${user.id}`)
        .on('postgres_changes', {
          event: 'UPDATE',
          schema: 'public',
          table: 'platform_orders',
          filter: `customer_id=eq.${user.id}`,
        }, (payload) => {
          const updated = payload.new as Order;
          setOrders(prev => prev.map(o => o.id === updated.id ? { ...o, ...updated } : o));
          // Flash the updated order
          setUpdatedFlash(updated.id);
          setTimeout(() => setUpdatedFlash(null), 4000);
        })
        .on('postgres_changes', {
          event: 'INSERT',
          schema: 'public',
          table: 'platform_orders',
          filter: `customer_id=eq.${user.id}`,
        }, (payload) => {
          const newOrder = payload.new as Order;
          setOrders(prev => {
            if (prev.some(o => o.id === newOrder.id)) return prev;
            return [newOrder, ...prev];
          });
        })
        .subscribe();
    });

    return () => {
      mounted = false;
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [router, fetchOrders]);

  const filtered = orders.filter(o => {
    const matchTab = activeTab === 'all' || o.status === activeTab;
    const matchSearch = !search || o.order_number.toLowerCase().includes(search.toLowerCase()) || (o.items_summary || '').toLowerCase().includes(search.toLowerCase());
    return matchTab && matchSearch;
  });

  return (
    <CustomerDashboardLayout>
      <div className="space-y-6">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-700 text-slate-900">My Orders</h1>
            <span className="flex items-center gap-1 text-xs font-500 text-green-600 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
              Live
            </span>
          </div>
          <p className="text-sm text-slate-500 mt-0.5">Track and manage all your orders</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 overflow-x-auto pb-1">
          {STATUS_TABS.map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-xl text-xs font-600 whitespace-nowrap transition-colors ${
                activeTab === tab ? 'bg-primary text-white' : 'bg-white border border-slate-200 text-slate-600 hover:border-primary/40'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
              {tab !== 'all' && (
                <span className="ml-1.5 opacity-70">
                  ({orders.filter(o => o.status === tab).length})
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="relative max-w-sm">
            <Icon name="MagnifyingGlassIcon" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search by order number or items…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
        </div>

        {/* Orders list */}
        <div className="space-y-3">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Icon name="ArrowPathIcon" size={28} className="animate-spin text-primary" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-xl py-16 text-center">
              <Icon name="ShoppingBagIcon" size={32} className="text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-400">No orders found</p>
            </div>
          ) : (
            filtered.map(order => (
              <div
                key={order.id}
                className={`border rounded-xl overflow-hidden transition-all duration-300 ${
                  updatedFlash === order.id
                    ? 'border-blue-400 shadow-md shadow-blue-100 bg-blue-50'
                    : 'bg-white border-slate-200'
                }`}
              >
                {updatedFlash === order.id && (
                  <div className="bg-blue-500 text-white text-xs font-semibold px-4 py-1.5 flex items-center gap-1.5">
                    <Icon name="ArrowPathIcon" size={12} />
                    Order status updated!
                  </div>
                )}
                <div
                  className="px-5 py-4 flex items-center justify-between gap-4 cursor-pointer hover:bg-slate-50 transition-colors"
                  onClick={() => setExpanded(expanded === order.id ? null : order.id)}
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-700 text-slate-900">#{order.order_number}</p>
                      <span className={`text-xs font-500 px-2.5 py-0.5 rounded-full ${STATUS_COLORS[order.status] || 'bg-gray-100 text-gray-600'}`}>
                        {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                      </span>
                    </div>
                    <p className="text-xs text-slate-400 mt-0.5 truncate">{order.items_summary || `${order.items_count} item(s)`}</p>
                    <p className="text-xs text-slate-400">{new Date(order.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="text-base font-700 text-slate-900">₹{order.total_amount?.toLocaleString('en-IN')}</span>
                    <Icon name={expanded === order.id ? 'ChevronUpIcon' : 'ChevronDownIcon'} size={16} className="text-slate-400" />
                  </div>
                </div>

                {expanded === order.id && (
                  <div className="px-5 pb-5 border-t border-slate-100 pt-4 space-y-3">
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      {[
                        ['Payment Method', order.payment_method || '—'],
                        ['Payment Status', order.payment_status || '—'],
                        ['Tracking Number', order.tracking_number || 'Not assigned'],
                        ['Est. Delivery', order.estimated_delivery_time ? new Date(order.estimated_delivery_time).toLocaleDateString('en-IN') : '—'],
                      ].map(([label, value]) => (
                        <div key={label}>
                          <p className="text-xs text-slate-400">{label}</p>
                          <p className="font-500 text-slate-800 text-xs mt-0.5">{value}</p>
                        </div>
                      ))}
                    </div>
                    {order.delivery_address && (
                      <div>
                        <p className="text-xs text-slate-400">Delivery Address</p>
                        <p className="text-xs font-500 text-slate-800 mt-0.5">{order.delivery_address}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </CustomerDashboardLayout>
  );
}
